import React, { useEffect, useState, Children } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { useTheme } from '../providers/ThemeProvider';
import { TeacherSidebar } from '../TeacherSidebar';
import { MenuIcon, XIcon } from 'lucide-react';
export const PrivacyPolicyPage = () => {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Open sidebar by default on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gradient-to-r from-gray-50 to-gray-100'}`} data-id="element-5630">
      <Navbar data-id="element-5631" />

      {/* Mobile sidebar toggle */}
      <button onClick={toggleSidebar} className="lg:hidden fixed bottom-4 right-4 z-30 bg-deep-pink text-white p-3 rounded-full shadow-lg flex items-center justify-center" aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'} style={{
      minWidth: '56px',
      minHeight: '56px'
    }} data-id="element-5632">
        {isSidebarOpen ? <XIcon className="h-6 w-6" data-id="element-5633" /> : <MenuIcon className="h-6 w-6" data-id="element-5634" />}
      </button>

      {/* Left Sidebar */}
      <aside className={`w-64 fixed inset-y-0 pt-20 pb-16 ${isDark ? 'bg-navy-900/90 shadow-lg' : 'bg-white shadow-md'} z-20 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-id="element-5635">
        <TeacherSidebar darkMode={isDark} closeSidebar={() => setIsSidebarOpen(false)} data-id="element-5636" />
      </aside>

      {/* Main Content */}
      <div className={`pt-20 pb-16 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-64' : 'ml-0 lg:ml-64'}`} data-id="element-5637">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5638">
          <div className={`${isDark ? 'bg-navy-800' : 'bg-white'} rounded-xl shadow-lg p-8 md:p-12`} data-id="element-5639">
            <h1 className={`text-4xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5640">
              Privacy Policy
            </h1>

            <div className={`prose prose-lg max-w-none ${isDark ? 'prose-invert' : ''}`} data-id="element-5641">
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5642">
                <strong data-id="element-5643">Effective Date:</strong> January 15, 2025
              </p>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5644">
                MaestroConnect ("we," "us," or "our") is committed to protecting
                your privacy. This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you use our
                platform and services (collectively, the "Services"). By
                accessing or using the Services, you agree to the terms of this
                Privacy Policy.
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5645">
                1. Information We Collect
              </h2>

              <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5646">
                1.1 Information You Provide
              </h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-5647">
                We collect information you provide directly to us, including:
              </p>
              <ul className={`list-disc pl-6 mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5648">
                <li data-id="element-5649">
                  <strong data-id="element-5650">Account Information:</strong> Name, email address,
                  phone number, profile picture, and other registration details.
                </li>
                <li data-id="element-5651">
                  <strong data-id="element-5652">Profile Information:</strong> Teaching credentials,
                  instruments taught, availability, location, and other
                  professional details.
                </li>
                <li data-id="element-5653">
                  <strong data-id="element-5654">Payment Information:</strong> Payment card details,
                  billing address, and transaction history (processed securely
                  through third-party payment processors).
                </li>
                <li data-id="element-5655">
                  <strong data-id="element-5656">Communications:</strong> Messages, feedback, and other
                  communications you send through the Services.
                </li>
                <li data-id="element-5657">
                  <strong data-id="element-5658">Lesson and Practice Data:</strong> Lesson schedules,
                  attendance records, practice logs, progress notes, and other
                  educational content.
                </li>
              </ul>

              <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5659">
                1.2 Information Collected Automatically
              </h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-5660">
                When you use the Services, we automatically collect certain
                information, including:
              </p>
              <ul className={`list-disc pl-6 mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5661">
                <li data-id="element-5662">
                  <strong data-id="element-5663">Device Information:</strong> IP address, browser type,
                  operating system, device identifiers, and mobile network
                  information.
                </li>
                <li data-id="element-5664">
                  <strong data-id="element-5665">Usage Information:</strong> Pages viewed, features
                  used, time spent on the Services, and other usage statistics.
                </li>
                <li data-id="element-5666">
                  <strong data-id="element-5667">Location Information:</strong> Approximate location
                  based on IP address or precise location if you grant
                  permission.
                </li>
                <li data-id="element-5668">
                  <strong data-id="element-5669">Cookies and Tracking Technologies:</strong> We use
                  cookies, web beacons, and similar technologies to collect
                  information about your interactions with the Services.
                </li>
              </ul>

              <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5670">
                1.3 Information from Third Parties
              </h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5671">
                We may receive information about you from third parties, such as
                social media platforms (if you choose to connect your account),
                payment processors, and identity verification services.
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5672">
                2. How We Use Your Information
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-5673">
                We use the information we collect to:
              </p>
              <ul className={`list-disc pl-6 mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5674">
                <li data-id="element-5675">Provide, maintain, and improve the Services.</li>
                <li data-id="element-5676">
                  Facilitate connections between teachers and students,
                  including matching and scheduling.
                </li>
                <li data-id="element-5677">
                  Process payments, manage subscriptions, and send transaction
                  confirmations.
                </li>
                <li data-id="element-5678">
                  Communicate with you about your account, lessons, and updates
                  to the Services.
                </li>
                <li data-id="element-5679">
                  Personalize your experience and provide tailored content and
                  recommendations.
                </li>
                <li data-id="element-5680">
                  Monitor and analyze usage trends to improve the Services.
                </li>
                <li data-id="element-5681">
                  Detect, prevent, and address fraud, security issues, and
                  technical problems.
                </li>
                <li data-id="element-5682">
                  Comply with legal obligations and enforce our Terms of
                  Service.
                </li>
                <li data-id="element-5683">
                  Send marketing communications (with your consent, where
                  required).
                </li>
              </ul>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5684">
                3. How We Share Your Information
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-5685">
                We do not sell your personal information. We may share your
                information in the following circumstances:
              </p>

              <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5686">
                3.1 With Other Users
              </h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5687">
                Teachers and students can view each other's profile information,
                lesson schedules, and communications necessary to facilitate
                lessons.
              </p>

              <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5688">
                3.2 With Service Providers
              </h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5689">
                We share information with third-party service providers who
                perform services on our behalf, such as payment processing,
                hosting, analytics, customer support, and marketing.
              </p>

              <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5690">
                3.3 For Legal Reasons
              </h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5691">
                We may disclose your information if required by law, legal
                process, or government request, or if we believe disclosure is
                necessary to protect our rights, your safety, or the safety of
                others.
              </p>

              <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5692">
                3.4 Business Transfers
              </h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5693">
                If we are involved in a merger, acquisition, or sale of assets,
                your information may be transferred as part of that transaction.
              </p>

              <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5694">
                3.5 With Your Consent
              </h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5695">
                We may share your information with third parties when you
                provide explicit consent.
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5696">
                4. Data Security
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5697">
                We implement reasonable security measures to protect your
                information from unauthorized access, disclosure, alteration,
                and destruction. However, no method of transmission over the
                internet or electronic storage is 100% secure. While we strive
                to protect your information, we cannot guarantee its absolute
                security.
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5698">
                5. Data Retention
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5699">
                We retain your information for as long as necessary to provide
                the Services, comply with legal obligations, resolve disputes,
                and enforce our agreements. When your information is no longer
                needed, we will securely delete or anonymize it.
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5700">
                6. Your Rights and Choices
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-5701">
                Depending on your location, you may have the following rights:
              </p>
              <ul className={`list-disc pl-6 mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5702">
                <li data-id="element-5703">
                  <strong data-id="element-5704">Access and Portability:</strong> Request a copy of
                  your personal information.
                </li>
                <li data-id="element-5705">
                  <strong data-id="element-5706">Correction:</strong> Update or correct inaccurate
                  information.
                </li>
                <li data-id="element-5707">
                  <strong data-id="element-5708">Deletion:</strong> Request deletion of your personal
                  information, subject to legal and contractual obligations.
                </li>
                <li data-id="element-5709">
                  <strong data-id="element-5710">Opt-Out:</strong> Unsubscribe from marketing
                  communications by following the instructions in the email or
                  contacting us.
                </li>
                <li data-id="element-5711">
                  <strong data-id="element-5712">Cookie Management:</strong> Adjust your browser
                  settings to refuse cookies or alert you when cookies are being
                  sent.
                </li>
              </ul>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5713">
                To exercise these rights, please contact us at{' '}
                <a href="mailto:privacy@maestroconnect.com" className="text-deep-pink hover:underline" data-id="element-5714">
                  privacy@maestroconnect.com
                </a>
                .
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5715">
                7. Children's Privacy
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5716">
                The Services are not intended for children under 13 years of
                age. We do not knowingly collect personal information from
                children under 13. If we learn that we have collected
                information from a child under 13, we will delete it promptly.
                If you believe we have collected information from a child under
                13, please contact us.
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5717">
                8. International Data Transfers
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5718">
                Your information may be transferred to and processed in
                countries other than your country of residence. These countries
                may have data protection laws that differ from those in your
                country. By using the Services, you consent to the transfer of
                your information to these countries.
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5719">
                9. Third-Party Links
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5720">
                The Services may contain links to third-party websites or
                services. We are not responsible for the privacy practices of
                these third parties. We encourage you to review their privacy
                policies before providing any information.
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5721">
                10. Changes to This Privacy Policy
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`} data-id="element-5722">
                We may update this Privacy Policy from time to time. If we make
                material changes, we will notify you by email or by posting a
                notice on the Services. Your continued use of the Services after
                the changes take effect constitutes your acceptance of the
                updated Privacy Policy.
              </p>

              <h2 className={`text-2xl font-semibold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5723">
                11. Contact Us
              </h2>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-5724">
                If you have questions or concerns about this Privacy Policy or
                our data practices, please contact us:
              </p>
              <div className={`${isDark ? 'bg-navy-700' : 'bg-gray-50'} p-6 rounded-lg mb-6`} data-id="element-5725">
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2`} data-id="element-5726">
                  <strong data-id="element-5727">Email:</strong>{' '}
                  <a href="mailto:privacy@maestroconnect.com" className="text-deep-pink hover:underline" data-id="element-5728">
                    privacy@maestroconnect.com
                  </a>
                </p>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2`} data-id="element-5729">
                  <strong data-id="element-5730">Mail:</strong> MaestroConnect, [Address], [City,
                  State, ZIP]
                </p>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5731">
                  <strong data-id="element-5732">Phone:</strong> [Phone Number]
                </p>
              </div>

              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mt-8`} data-id="element-5733">
                Thank you for trusting MaestroConnect with your information. We
                are committed to protecting your privacy and providing a safe,
                secure platform for music education.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile when sidebar is open */}
      {isSidebarOpen && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={toggleSidebar} aria-hidden="true" data-id="element-5734"></div>}

      <Footer data-id="element-5735" />
    </div>;
};